import * as React from 'react'
import { Pencil, Plus, X } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ExpandingIconButton } from '@/components/ui/expanding-icon-button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { ReminderTouch, RenewalSettings } from '@/pages/RenewalSettingsPage'
import { LevelRenewalOverrides } from '@/pages/level-renewal-overrides'
import type { MembershipLevel } from '@/pages/MembershipLevelsPage.types'

export interface LevelRenewalSettingsDraft {
  renewal_grace_days_override: number | null
  renewal_reminder_touches_override: Array<{ id?: string; offset: number }> | null
}

export interface GraceChangeImpact {
  count: number
  members: Array<{ person_id: number; name: string }>
  confirmation_token: string
}

export function RenewalSettingsPanel({
  armingCount,
  autoLapseEnabled,
  fieldErrors,
  graceDays,
  impact,
  onGraceDaysChange,
  onReviewArming,
  onSetAutoLapseEnabled,
  onShowAffectedMembers,
  onTouchesChange,
  saved,
  scheduleErrors,
  settings,
  touches,
  levels,
  levelDrafts,
  levelErrors,
  onLevelDraftChange,
}: {
  armingCount?: number
  autoLapseEnabled: boolean
  fieldErrors: string[]
  graceDays: string
  impact: GraceChangeImpact | null
  onGraceDaysChange: (value: string) => void
  onReviewArming?: () => void
  onSetAutoLapseEnabled: (enabled: boolean) => void
  onShowAffectedMembers: () => void
  onTouchesChange: (touches: ReminderTouch[]) => void
  saved: boolean
  scheduleErrors: string[]
  settings: RenewalSettings
  touches: ReminderTouch[]
  levels: MembershipLevel[]
  levelDrafts: Record<number, LevelRenewalSettingsDraft>
  levelErrors: string[]
  onLevelDraftChange: (level: MembershipLevel, draft: LevelRenewalSettingsDraft) => void
}) {
  const [dialogTouch, setDialogTouch] = React.useState<ReminderTouch | null | undefined>(undefined)
  const [offsetInput, setOffsetInput] = React.useState('')
  const nextDraftKey = React.useRef(0)

  function openTouchDialog(touch: ReminderTouch | null) {
    setDialogTouch(touch)
    setOffsetInput(touch?.offset.toString() ?? '')
  }

  function saveTouch() {
    const offset = Number(offsetInput)
    if (!Number.isInteger(offset)) return

    onTouchesChange(
      dialogTouch
        ? touches.map((touch) => (touch === dialogTouch ? { ...touch, offset } : touch))
        : [...touches, { offset, kind: 'reminder', draftKey: nextDraftKey.current++ }],
    )
    setDialogTouch(undefined)
  }

  return (
    <>
      <CardContent className="divide-y divide-border p-0">
        <ReminderSchedule
          leadDays={settings.renewal_invoice_lead_days}
          onEdit={openTouchDialog}
          onRemove={(touch) => onTouchesChange(touches.filter((item) => item !== touch))}
          onStartAdd={() => openTouchDialog(null)}
          scheduleErrors={scheduleErrors}
          touches={touches}
        />
        <GraceAndAutoLapse
          armingCount={armingCount}
          autoLapseEnabled={autoLapseEnabled}
          fieldErrors={fieldErrors}
          graceDays={graceDays}
          impact={impact}
          onGraceDaysChange={onGraceDaysChange}
          onReviewArming={onReviewArming}
          onSetAutoLapseEnabled={onSetAutoLapseEnabled}
          onShowAffectedMembers={onShowAffectedMembers}
          saved={saved}
        />
        <LevelRenewalOverrides
          levels={levels}
          drafts={levelDrafts}
          errors={levelErrors}
          associationGraceDays={settings.renewal_grace_days}
          associationTouches={settings.renewal_reminder_touches}
          onDraftChange={onLevelDraftChange}
        />
      </CardContent>
      <ReminderDialog
        offsetInput={offsetInput}
        setOffsetInput={setOffsetInput}
        touch={dialogTouch}
        onClose={() => setDialogTouch(undefined)}
        onSave={saveTouch}
      />
    </>
  )
}

function ReminderSchedule({
  leadDays,
  onEdit,
  onRemove,
  onStartAdd,
  scheduleErrors,
  touches,
}: {
  leadDays: number
  onEdit: (touch: ReminderTouch) => void
  onRemove: (touch: ReminderTouch) => void
  onStartAdd: () => void
  scheduleErrors: string[]
  touches: ReminderTouch[]
}) {
  return (
    <section className="px-5 py-4" aria-labelledby="reminder-schedule-heading">
      <SectionLabel id="reminder-schedule-heading">Reminder schedule</SectionLabel>
      <div className="divide-y divide-border">
        <ReminderRow
          label={`${leadDays} days before due`}
          detail="Invoice ready"
          actions={
            <span className="text-caption text-muted-foreground">
              Sent when the invoice is raised
            </span>
          }
        />
        {touches.map((touch) => (
          <ReminderRow
            key={touch.id ?? `new-${touch.draftKey}`}
            label={offsetLabel(touch.offset)}
            detail={touch.kind === 'final_warning' ? 'Final warning' : 'Reminder'}
            actions={<ReminderActions touch={touch} onEdit={onEdit} onRemove={onRemove} />}
          />
        ))}
      </div>
      <Button
        type="button"
        variant="outline"
        className="mt-2 border-dashed text-muted-foreground"
        onClick={onStartAdd}
      >
        <Plus className="mr-1 size-3.5" strokeWidth={1.5} /> Add reminder
      </Button>
      {scheduleErrors.map((message) => (
        <p className="mt-2 text-body-sm text-destructive" key={message}>
          {message}
        </p>
      ))}
    </section>
  )
}

function ReminderActions({
  touch,
  onEdit,
  onRemove,
}: {
  touch: ReminderTouch
  onEdit: (touch: ReminderTouch) => void
  onRemove: (touch: ReminderTouch) => void
}) {
  const label = offsetLabel(touch.offset)
  return (
    <div className="flex items-center gap-0.5">
      <ExpandingIconButton
        icon={Pencil}
        label={`Edit ${label} reminder`}
        onClick={() => onEdit(touch)}
      />
      <ExpandingIconButton
        icon={X}
        label={`Remove ${label} reminder`}
        onClick={() => onRemove(touch)}
      />
    </div>
  )
}

function GraceAndAutoLapse({
  armingCount,
  autoLapseEnabled,
  fieldErrors,
  graceDays,
  impact,
  onGraceDaysChange,
  onReviewArming,
  onSetAutoLapseEnabled,
  onShowAffectedMembers,
  saved,
}: {
  armingCount?: number
  autoLapseEnabled: boolean
  fieldErrors: string[]
  graceDays: string
  impact: GraceChangeImpact | null
  onGraceDaysChange: (value: string) => void
  onReviewArming?: () => void
  onSetAutoLapseEnabled: (enabled: boolean) => void
  onShowAffectedMembers: () => void
  saved: boolean
}) {
  return (
    <section className="px-5 py-4" aria-labelledby="grace-auto-lapse-heading">
      <SectionLabel id="grace-auto-lapse-heading">Grace &amp; auto-lapse</SectionLabel>
      <div className="flex items-center gap-2 py-2">
        <Label htmlFor="grace-period" className="w-44 shrink-0">
          Grace period
        </Label>
        <Input
          id="grace-period"
          className="h-8 w-16"
          inputMode="numeric"
          value={graceDays}
          onChange={(event) => onGraceDaysChange(event.target.value)}
        />
        <span className="text-body-sm text-muted-foreground">days past the due date</span>
      </div>
      {!impact &&
        fieldErrors.map((message) => (
          <p className="ml-44 text-body-sm text-destructive" key={message}>
            {message}
          </p>
        ))}
      {impact && (
        <GraceChangeWarning
          graceDays={graceDays}
          impact={impact}
          onShowAffectedMembers={onShowAffectedMembers}
        />
      )}
      {autoLapseEnabled ? (
        <div className="flex items-start py-2">
          <Checkbox
            id="auto-lapse"
            checked
            onCheckedChange={(checked) => onSetAutoLapseEnabled(checked === true)}
          />
          <Label htmlFor="auto-lapse" className="ml-2">
            Lapse automatically when grace ends
          </Label>
        </div>
      ) : (
        <div className="flex items-center gap-2 py-2">
          <Button type="button" variant="outline" className="h-8" onClick={onReviewArming}>
            Turn on auto-lapse…
          </Button>
          {armingCount !== undefined && (
            <span className="text-body-sm text-muted-foreground">
              {overdueCountLabel(armingCount)}
            </span>
          )}
        </div>
      )}
      {saved && <p className="pt-2 text-body-sm text-foreground">Renewal settings saved.</p>}
    </section>
  )
}

function GraceChangeWarning({
  graceDays,
  impact,
  onShowAffectedMembers,
}: {
  graceDays: string
  impact: GraceChangeImpact
  onShowAffectedMembers: () => void
}) {
  return (
    <Alert variant="warning" className="mt-2 flex items-center gap-2 text-body-sm">
      <span>
        Shortening grace to {graceDays} days lapses {impact.count} {memberWord(impact.count)} at
        tonight&apos;s run.
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-auto border-0 bg-transparent px-0 py-0 text-caption text-muted-foreground shadow-none underline-offset-4 hover:bg-transparent hover:text-foreground hover:underline"
        onClick={onShowAffectedMembers}
      >
        View the {impact.count} {memberWord(impact.count)}
      </Button>
    </Alert>
  )
}

function ReminderDialog({
  offsetInput,
  setOffsetInput,
  touch,
  onClose,
  onSave,
}: {
  offsetInput: string
  setOffsetInput: (value: string) => void
  touch: ReminderTouch | null | undefined
  onClose: () => void
  onSave: () => void
}) {
  return (
    <Dialog open={touch !== undefined} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{touch ? 'Edit reminder' : 'Add reminder'}</DialogTitle>
          <DialogDescription>
            Set the number of whole days before or after renewal-due.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Label htmlFor="reminder-timing" className="w-32 shrink-0">
            Reminder timing
          </Label>
          <Input
            id="reminder-timing"
            className="h-8 w-20"
            inputMode="numeric"
            value={offsetInput}
            onChange={(event) => setOffsetInput(event.target.value)}
          />
          <span className="text-body-sm text-muted-foreground">days from due</span>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={offsetInput.trim() === '' || !Number.isInteger(Number(offsetInput))}
            onClick={onSave}
          >
            {touch ? 'Save reminder' : 'Add reminder to schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SectionLabel({ children, id }: { children: string; id: string }) {
  return (
    <h2
      className="mb-2 text-caption font-medium uppercase tracking-wide text-muted-foreground"
      id={id}
    >
      {children}
    </h2>
  )
}

function ReminderRow({
  label,
  detail,
  actions,
}: {
  label: string
  detail: string
  actions?: React.ReactNode
}) {
  return (
    <div className="flex min-h-12 items-center py-2">
      <span className="w-44 shrink-0 text-body-sm text-foreground">{label}</span>
      <span className="text-body-sm text-muted-foreground">{detail}</span>
      <div className="ml-auto pl-4">{actions}</div>
    </div>
  )
}

function offsetLabel(offset: number) {
  if (offset === 0) return 'On due date'
  return `${Math.abs(offset)} days ${offset < 0 ? 'before' : 'after'} due`
}

function overdueCountLabel(count: number) {
  return `${count} ${count === 1 ? 'member is' : 'members are'} currently overdue`
}

function memberWord(count: number) {
  return count === 1 ? 'member' : 'members'
}
